// read https://medium.com/simply/state-management-with-react-hooks-and-context-api-at-10-lines-of-code-baf6be8302c
import React, {
  createContext, useContext, useReducer
} from 'react';
import PropTypes from 'prop-types';
import { EditorState } from 'draft-js';
import { useReactContextDevTool } from 'hooks'

export const EditorContext = createContext();

export const status = {
  none: 'none',
  draft: 'draft',
  published: 'published',
  prePublished: 'prePublished',
  archive: 'archive',
  deleted: 'deleted',
  edited: 'edited',
  local: 'local',
  error: 'error'
}

// initial state
const initState = {
  // live editor data
  pageLiveData: null,
  // data from the server
  pageSavedData: [],
  pageOtherLanguageData: null,
  pageLoading: false,
  pageError: false,
  sectionActive: null,
  pageEdited: false,
  pageTitle: 'Página',
  pageStatus: status.none,
  pageTempSlug: null,
  pageSlug: null,
  screen: 'desktop',
  richTextEditorState: EditorState.createEmpty(),
  richTextEditorStateTrigger: false,
  sidenav: null,
  sidenavPrevious: null,
  moduleBeingEdited: null,
  moduleBeingEditedName: null,
  pageTriggerRefetch: false,
  idimg: null,
  urlimg: null,
  peopleModuleselected: []
};

// actions
export const actions = {
  PAGE_DATA_UPDATE: 'PAGE_DATA_UPDATE',
  PAGE_DATA_LOADING: 'PAGE_DATA_LOADING',
  PAGE_DATA_PUBLISH: 'PAGE_DATA_PUBLISH',
  PAGE_DATA_PRE_PUBLISH: 'PAGE_DATA_PRE_PUBLISH',
  PAGE_DATA_SAVE: 'PAGE_DATA_SAVE',
  PAGE_ADD: 'PAGE_ADD',
  PAGE_ARCHIVE: 'PAGE_ARCHIVE',
  PAGE_DELETE: 'PAGE_DELETE',
  PAGE_REFETCH: 'PAGE_REFETCH',
  PAGE_TITLE_UPDATE: 'PAGE_TITLE_UPDATE',
  SECTION_SET_ACTIVE: 'SECTION_SET_ACTIVE',
  SECTION_SET_INACTIVE: 'SECTION_SET_INACTIVE',
  SCREEN_SET: 'SCREEN_SET',
  RICH_TEXT_EDITOR_STATE_UPDATE: 'RICH_TEXT_EDITOR_STATE_UPDATE',
  RICH_TEXT_EDITOR_STATE_TRIGGER: 'RICH_TEXT_EDITOR_STATE_TRIGGER',
  SIDENAV_UPDATE: 'SIDENAV_UPDATE',
  SET_MENU_OPEN: 'SET_MENU_OPEN',
  PAGE_SLUG_UPDATE: 'PAGE_SLUG_UPDATE',
  IMG_RENDER: 'IMG_RENDER',
  PEOPLE_MODULE_SELECT: 'PEOPLE_MODULE_SELECT'
};

// reducer
const reducer = (state, action) => {
  const { type, data } = action;
  switch (type) {
  case actions.PAGE_ADD:
    return {
      ...state,
      pageLiveData: data.liveData,
      pageSavedData: data.savedData,
      pageEdited: false,
      pageStatus: (data.status && data.status in status) ? status[data.status] : status.error,
      pageSlug: data.slug ? data.slug : initState.pageSlug,
      pageOtherLanguageData: data.otherLanguageContent,
      pageTempSlug: null
    };
  case actions.PAGE_DATA_LOADING:
    return {
      ...state,
      pageLoading: data,
    };
  case actions.PAGE_DATA_UPDATE:
    return {
      ...state,
      pageLiveData: data,
      pageEdited: true,
      pageStatus: status.local,
      pageTempSlug: null
    };
  case actions.PAGE_DATA_SAVE:
    return {
      ...state,
      pageLiveData: data,
      pageSavedData: data,
      pageEdited: true,
      pageStatus: status.edited,
      pageTempSlug: null
    };
  case actions.PAGE_DATA_PUBLISH:
    return {
      ...state,
      pageLiveData: data,
      pageSavedData: data,
      pageEdited: false,
      pageStatus: status.published,
      pageTempSlug: null
    };
  case actions.PAGE_DATA_PRE_PUBLISH:
    return {
      ...state,
      pageStatus: status.prePublished,
      pageTempSlug: data.url
    };
  case actions.PAGE_REFETCH:
    return {
      ...state,
      pageTriggerRefetch: !state.pageTriggerRefetch,
      pageTempSlug: null
    };
  case actions.PAGE_ARCHIVE:
    return {
      ...state,
      pageEdited: false,
      pageStatus: status.archive,
      pageTempSlug: null
    };
  case actions.PAGE_DELETE:
    return {
      ...initState
    };
  case actions.PAGE_TITLE_UPDATE:
    return {
      ...state,
      pageTitle: data,
      pageTempSlug: null
    };
  case actions.SECTION_SET_ACTIVE:
    return {
      ...state,
      sectionActive: data,
    };
  case actions.SECTION_SET_INACTIVE:
    return {
      ...state,
      sectionActive: data === state.sectionActive ? null : state.sectionActive,
    };
  case actions.SCREEN_SET:
    return {
      ...state,
      screen: data,
    };
  case actions.RICH_TEXT_EDITOR_STATE_UPDATE:
    return {
      ...state,
      richTextEditorState: data,
    };
  case actions.RICH_TEXT_EDITOR_STATE_TRIGGER:
    return {
      ...state,
      richTextEditorState: data,
      richTextEditorStateTrigger: !state.richTextEditorStateTrigger
    };
  case actions.SIDENAV_UPDATE:
    return {
      ...state,
      sidenav: data.new,
      sidenavPrevious: data.previous,
    };
  case actions.SET_MODULE_EDITED: {
    return {
      ...state,
      moduleBeingEdited: data.key,
      moduleBeingEditedName: data.name,
    };
  }
  case actions.PAGE_SLUG_UPDATE:
    return {
      ...state,
      pageSlug: data,
    };
  case actions.IMG_RENDER:
    return {
      ...state,
      idimg: data.idimg,
      urlimg: data.urlimg
    }
  case actions.PEOPLE_MODULE_SELECT:
    return {
      ...state,
      peopleModuleselected: data,
    };
  default:
    return state;
  }
};

export const EditorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initState)
  useReactContextDevTool({ id: 'editor', displayName: 'Editor', values: state })

  return (
    <EditorContext.Provider value={[state, dispatch]}>
      {children}
    </EditorContext.Provider>
  );
}

EditorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useEditorContext = () => useContext(EditorContext);
